import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Logout from '../components/common/Logout';
import { SpinLoader } from '../components/common/SpinLoader';
import { PermissionEnum } from '../enums/PermissionEnum';
import PageContentLayout from '../layouts/PageContentLayout';
import SharedLayout from '../layouts/SharedLayout';
import PrivateRoute from './privateRoute';
import { usePermissions } from '../hooks/usePermissions';
import { RoleName } from '../enums/RoleNameEnm';

import AccessManagementPage from '../pages/accessManagement/views/AccessManagementPage';
import ProfilePage from '../pages/profile/views/Profile';
import UserManagementPage from '../pages/userManagement/views/UserManagementPage';
import HomePage from '../pages/home/HomePage';
import SignInPage from '../pages/signIn/views/SignInPage';
import CitizenPortal from '../pages/citizenPortal/CitizenPortal';
import AnalysisAndReports from '../pages/analysisAndReports/AnalysisAndReports';
import Administration from '../pages/administration/Administration';
import AboutUs from '../pages/aboutUs/AboutUs';
import ContactUs from '../pages/contactUs/ContactUs';
import PriceView from '../pages/price/views/Price';
import Inspections from '../pages/inspector/myPrices/MyPrices';
import InspectorPortal from '../pages/inspector/InspectorPortal';
import AdvancedAnalysis from '../pages/advancedAnalysis/AdvancedAnalysis';
import CitizenRegistrationPage from '../pages/register/views/CitizenRegistrationPage';
import NewsList from '../pages/news/NewsList';
import NewsDetail from '../pages/news/NewsDetail';
import CitizenPriceView from '../pages/price/views/CitizenPriceView';
import NewsLetterView from '../pages/newsLetterView/NewsLetterView';
import ScaleFormatVerification from '../pages/subtaskInspections/scaleVerificationInspections/ScaleFormatVerificationForm';
import FuelDispenserVerificationForm from '../pages/subtaskInspections/fuelPumpVerificationInspections/FuelDispenserVerfication';
import PrePackagedProductVerificationForm from '../pages/subtaskInspections/prePackagedProductVerify/PrePackagedProductVerificationForm';

const InternalRoutes: React.FC = () => {
  const { hasPermission, hasRoles } = usePermissions();
  const canAccessInspector = hasPermission([
    PermissionEnum.RUN_INSPECTIONS,
    PermissionEnum.CREATE_INSPECTIONS,
    PermissionEnum.UPDATE_INSPECTIONS,
    PermissionEnum.VIEW_INSPECTIONS,
    PermissionEnum.DELETE_INSPECTIONS,
  ]);
  const canRunInspections = hasPermission(PermissionEnum.RUN_INSPECTIONS);
  const canManageNewsletters = hasPermission(PermissionEnum.MANAGE_NEWSLETTERS);
  const canAccessAdministration =
    hasRoles([RoleName.ADMIN, RoleName.SYSTEM_ADMIN]) &&
    hasPermission([
      PermissionEnum.VIEW_USER_ROLE,
      PermissionEnum.MANAGE_USER,
      PermissionEnum.CREATE_PRODUCTS,
      PermissionEnum.VIEW_PRODUCTS,
      PermissionEnum.CREATE_PRESENTATION,
      PermissionEnum.VIEW_PRESENTATION,
      PermissionEnum.UPDATE_PRESENTATION,
      PermissionEnum.DELETE_PRESENTATION,
      PermissionEnum.VIEW_PRODUCT_CATEGORIES,
      PermissionEnum.MANAGE_PRODUCT_CATEGORIES,
      PermissionEnum.MANAGE_BASKETS,
      PermissionEnum.VIEW_BASKETS,
      PermissionEnum.VIEW_CONFIG_ALERTS,
      PermissionEnum.CONFIG_ALERTS,
      PermissionEnum.MANAGE_NEWS,
      PermissionEnum.MANAGE_NEWSLETTERS,
    ]);

  return (
    <Routes>
      <Route path="/signIn" element={<SignInPage></SignInPage>}></Route>

      <Route path="/" element={<SharedLayout />}>
        <Route index element={<HomePage />} />
        <Route path="register" element={<CitizenRegistrationPage />} />
        <Route
          path="home"
          element={
            <Suspense fallback={<SpinLoader />}>
              <HomePage />
            </Suspense>
          }
        />
        <Route
          path="citizenPortal"
          element={
            <Suspense fallback={<SpinLoader />}>
              <CitizenPortal />
            </Suspense>
          }
        />
        <Route
          path="aboutUs"
          element={
            <Suspense fallback={<SpinLoader />}>
              <AboutUs />
            </Suspense>
          }
        />
        <Route
          path="contactUs"
          element={
            <Suspense fallback={<SpinLoader />}>
              <ContactUs />
            </Suspense>
          }
        />
        <Route
          path="news"
          element={
            <Suspense fallback={<SpinLoader />}>
              <NewsList />
            </Suspense>
          }
        />
        <Route
          path="news/:id"
          element={
            <Suspense fallback={<SpinLoader />}>
              <NewsDetail />
            </Suspense>
          }
        />

        <Route
          path="home/newsletter/view/:id"
          element={
            <Suspense fallback={<SpinLoader />}>
              <NewsLetterView />
            </Suspense>
          }
        ></Route>

        <Route element={<PrivateRoute />}>
          {canManageNewsletters && (
            <Route
              path="administration/newsletter/view/:id"
              element={
                <Suspense fallback={<SpinLoader />}>
                  <NewsLetterView />
                </Suspense>
              }
            />
          )}

          <Route element={<PageContentLayout />}>
            {hasPermission([
              PermissionEnum.ANALYTICS,
              PermissionEnum.MANAGE_ANALYTICS,
              PermissionEnum.VIEW_ANALYTICS,
            ]) && (
              <Route
                path="analysis"
                element={
                  <Suspense fallback={<SpinLoader />}>
                    <AnalysisAndReports />
                  </Suspense>
                }
              />
            )}
            {hasPermission([
              PermissionEnum.ANALYTICS,
              PermissionEnum.MANAGE_ANALYTICS,
              PermissionEnum.VIEW_ANALYTICS,
              PermissionEnum.VIEW_ALERTS,
              PermissionEnum.VIEW_CONFIG_ALERTS,
              PermissionEnum.CONFIG_ALERTS,
            ]) && (
              <Route
                path="analysis/advanced"
                element={
                  <Suspense fallback={<SpinLoader />}>
                    <AdvancedAnalysis />
                  </Suspense>
                }
              />
            )}
            {canAccessAdministration && (
              <Route
                path="administration"
                element={
                  <Suspense fallback={<SpinLoader />}>
                    <Administration />
                  </Suspense>
                }
              />
            )}

            {canAccessInspector && (
              <Route
                path="inspector"
                element={
                  <Suspense fallback={<SpinLoader />}>
                    <InspectorPortal />
                  </Suspense>
                }
              />
            )}

            {canRunInspections && (
              <Route
                path="inspector/inspections"
                element={
                  <Suspense fallback={<SpinLoader />}>
                    <Inspections triggeredFrom={'inspectorPortal'} />
                  </Suspense>
                }
              />
            )}

            {hasPermission([PermissionEnum.USER_PERMISSION, PermissionEnum.VIEW_USER_PERMISSION]) &&
              hasPermission([PermissionEnum.VIEW_USER_ROLE, PermissionEnum.USER_ROLE]) && (
                <Route
                  path="settings/accessManagement"
                  element={
                    <Suspense fallback={<SpinLoader />}>
                      <AccessManagementPage />
                    </Suspense>
                  }
                />
              )}

            <Route
              path="profile"
              element={
                <Suspense fallback={<SpinLoader />}>
                  <ProfilePage />
                </Suspense>
              }
            ></Route>
            {hasPermission([
              PermissionEnum.USER,
              PermissionEnum.VIEW_USER,
              PermissionEnum.VIEW_USER_ROLE,
              PermissionEnum.USER_ROLE,
            ]) && (
              <Route
                path="settings/userManagement"
                element={
                  <Suspense fallback={<SpinLoader />}>
                    <UserManagementPage />
                  </Suspense>
                }
              />
            )}

            {canRunInspections && (
              <>
                <Route
                  path="inspector/price-record"
                  element={
                    <Suspense fallback={<SpinLoader />}>
                      <PriceView />
                    </Suspense>
                  }
                />

                <Route
                  path="inspector/scale-format-verification"
                  element={
                    <Suspense fallback={<SpinLoader />}>
                      <ScaleFormatVerification />
                    </Suspense>
                  }
                />

                <Route
                  path="inspector/fuel-dispenser-verification"
                  element={
                    <Suspense fallback={<SpinLoader />}>
                      <FuelDispenserVerificationForm />
                    </Suspense>
                  }
                />

                <Route
                  path="inspector/pre-packaged-product-verification"
                  element={
                    <Suspense fallback={<SpinLoader />}>
                      <PrePackagedProductVerificationForm />
                    </Suspense>
                  }
                />
              </>
            )}

            <Route
              path="price-record"
              element={
                <Suspense fallback={<SpinLoader />}>
                  {hasPermission([
                    PermissionEnum.RUN_INSPECTIONS,
                    // PermissionEnum.CREATE_INSPECTIONS,
                  ]) ? (
                    <PriceView />
                  ) : (
                    <CitizenPriceView />
                  )}
                </Suspense>
              }
            />

            <Route path="*" element={<Navigate to={'/'} replace />} />
          </Route>
        </Route>
      </Route>
      <Route path="/logout" element={<Logout></Logout>} />

      <Route path="*" element={<Navigate to={'/'} replace />} />
    </Routes>
  );
};

export default InternalRoutes;
